import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { MAX_REASON_LENGTH } from './constants'

export function AddFlagForm({ editingFlag, onSave, onCancelEdit }) {
  const [value, setValue] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const isEditMode = !!editingFlag
  const isEmpty = !value.trim()

  // Count Unicode code points (spread), not UTF-16 units, so the on-screen
  // count matches the backend's Ruby String#length. A newline counts as one.
  const charCount = [...value].length
  const isOverLimit = charCount > MAX_REASON_LENGTH

  const textareaRef = useRef(null)

  const editingFlagId = editingFlag?.id

  useEffect(() => {
    if (!editingFlagId) {
      setValue('')
      return
    }
    setValue(editingFlag.reason ?? '')
    textareaRef.current?.focus() // Auto-focus textarea when switching into edit mode
  }, [editingFlagId])

  async function handleSubmit(e) {
    e.preventDefault()
    if (isEmpty || submitting || isOverLimit) return
    setSubmitting(true)
    try {
      await onSave?.(value, editingFlag?.id)
      if (!isEditMode) setValue('')
    } finally {
      setSubmitting(false)
    }
  }

  function handleCancel() {
    setValue('')
    onCancelEdit?.()
  }

  const submitClass = clsx(
    'dome-btn dome-btn-base',
    submitting || isEmpty || isOverLimit ? 'dome-btn-disabled' : 'dome-btn-aqua-stroke'
  )

  return (
    <div className="flag-add-form">
      <form onSubmit={handleSubmit}>
        <textarea
          className="flag-add-form__editor"
          placeholder="enter a note about incomplete information here..."
          value={value}
          ref={textareaRef}
          onChange={(e) => setValue(e.target.value)}
          disabled={submitting}
          aria-label="Flag note"
        />

        <div className="flag-add-form__meta">
          {isOverLimit && (
            <span className="flag-add-form__warning" role="alert">
              This reason is too long. The maximum is {MAX_REASON_LENGTH} characters.
            </span>
          )}
          <span
            className={clsx('flag-add-form__counter', isOverLimit && 'flag-add-form__counter--over')}
            aria-live="polite"
          >
            {charCount} / {MAX_REASON_LENGTH}
          </span>
        </div>

        <div className="flag-add-form__actions">
          {isEditMode ? (
            <div className="dome-full-width dome-d-flex dome-justify-between">
              <button
                type="button"
                onClick={handleCancel}
                disabled={submitting}
                className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-chevron-left"
              >
                back
              </button>
              <button type="submit" disabled={submitting || isEmpty || isOverLimit} className={submitClass}>
                save edits
              </button>
            </div>
          ) : (
            <div className="dome-d-flex dome-justify-end">
              <button type="submit" disabled={submitting || isEmpty || isOverLimit} className={submitClass}>
                add note
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  )
}
